import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Search, ShieldCheck } from 'lucide-react';

import { Input } from '@/components/ui/Input';
import { Card, CardContent } from '@/components/ui/Card';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { EmptyState } from '@/components/data/EmptyState';
import { AuditChip } from '@/components/audit/AuditChip';
import { DateTime } from '@/components/data/DateTime';
import { Badge } from '@/components/ui/Badge';
import { useUserSearch } from '@/queries/users';
import { useApiError } from '@/lib/useApiError';
import type { UserListItem } from '@/schemas/admin/users';

const searchSchema = z.object({ q: z.string().optional() });

export const Route = createFileRoute('/_authed/users/')({
  validateSearch: searchSchema,
  component: UsersListScreen,
});

function UsersListScreen() {
  const { t } = useTranslation('users');
  const { t: tc } = useTranslation('common');
  const search = Route.useSearch();
  const navigate = useNavigate();
  const resolveError = useApiError();

  const [draft, setDraft] = React.useState(search.q ?? '');

  // Debounce: only search after the operator pauses typing.
  React.useEffect(() => {
    const id = window.setTimeout(() => {
      const value = draft.trim();
      const current = search.q ?? '';
      if (value === current) return;
      void navigate({
        to: '/users',
        search: value ? { q: value } : {},
        replace: true,
      });
    }, 250);
    return () => window.clearTimeout(id);
  }, [draft, navigate, search.q]);

  const query = useUserSearch(search.q ?? '');
  const errorInfo = query.error ? resolveError(query.error) : null;

  const columns: readonly DataColumn<UserListItem>[] = [
    {
      id: 'handle',
      header: t('columns.handle'),
      cell: (row) => (
        <span className="flex flex-wrap items-center gap-2">
          <Link
            to="/users/$id"
            params={{ id: row.id }}
            className="font-medium text-[color:var(--color-accent)] hover:underline"
          >
            {row.handle}
          </Link>
          {row.is_admin ? (
            <Badge tone="accent">
              <ShieldCheck className="size-3" aria-hidden />
              {t('admin.badge')}
            </Badge>
          ) : null}
        </span>
      ),
      sortBy: (r) => r.handle,
    },
    {
      id: 'email',
      header: t('columns.emailMasked'),
      cell: (row) => (
        <span dir="ltr" className="font-mono text-xs text-[color:var(--color-fg-muted)]">
          {row.email_masked}
        </span>
      ),
    },
    {
      id: 'state',
      header: t('columns.state'),
      cell: (row) => (
        <Badge tone={row.state === 'active' ? 'success' : 'warn'}>{row.state}</Badge>
      ),
      sortBy: (r) => r.state,
    },
    {
      id: 'created',
      header: t('columns.created'),
      cell: (row) => <DateTime value={row.created_at} />,
      sortBy: (r) => new Date(r.created_at),
    },
    {
      id: 'id',
      header: t('columns.id'),
      cell: (row) => (
        <span dir="ltr" className="font-mono text-xs text-[color:var(--color-fg-subtle)]">
          {row.id.slice(0, 8)}…
        </span>
      ),
      hiddenInitially: true,
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
          <AuditChip />
        </div>
        <p className="text-sm text-[color:var(--color-fg-muted)]">{t('subtitle')}</p>
      </header>

      <Card>
        <CardContent className="pt-6">
          <label htmlFor="users-q" className="sr-only">
            {t('searchLabel')}
          </label>
          <div className="relative">
            <Search
              aria-hidden
              className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-[color:var(--color-fg-muted)]"
            />
            <Input
              id="users-q"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t('searchPlaceholder')}
              autoComplete="off"
              className="ps-9"
              dir="ltr"
            />
          </div>
          <p className="mt-2 text-xs text-[color:var(--color-fg-muted)]">{t('cap50Hint')}</p>
        </CardContent>
      </Card>

      {!search.q ? (
        <EmptyState title={t('searchPromptTitle')} body={t('searchPromptBody')} />
      ) : (
        <DataTable<UserListItem>
          data={query.data ?? []}
          columns={columns}
          rowKey={(r) => r.id}
          loading={query.isLoading}
          error={errorInfo}
          empty={{
            title: t('emptyTitle'),
            body: t('emptyBody'),
          }}
          caption={tc('audit.chipHint')}
          initialSort={{ id: 'created', direction: 'desc' }}
        />
      )}
    </div>
  );
}
